/**
 * Puerto Rico services-withholding (Sec. 1062.03) — client-side mirror of
 * backend/services/withholding.py.
 *
 * A payer of "services rendered" holds back a percentage and remits it to
 * Hacienda on TWM's behalf. It is NOT a discount — recognized revenue (the
 * invoice Total) is unchanged; only the customer's Balance Due drops. The
 * held-back figure shown to staff (order summary, invoice preview) must match
 * what the react-pdf invoice renders, so the arithmetic is kept identical to the
 * Python source of truth: resolve the effective rate, then round the holdback
 * half-up on the pre-tax subtotal (whole cents).
 *
 * Rate model (fractions, e.g. 0.10 = 10%):
 *   - order override (orders.pr_withholding_rate): null/undefined = inherit; a
 *     value INCLUDING 0 is an explicit per-invoice override.
 *   - customer default (customers.pr_withholding_rate): null = not a withholding
 *     agent; a value auto-applies at that rate.
 */

/** Totals-row label prefix (matches PR_WITHHOLDING_LABEL in the backend). */
export const PR_WITHHOLDING_LABEL = 'PR withholding';

const toRate = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Effective withholding rate for an invoice. The order override wins when
 * present (including an explicit 0, which zeroes out an otherwise-withholding
 * customer); otherwise the customer default; absent both → 0. Never negative.
 * Mirrors withholding.resolve_withholding_rate.
 */
export const resolveWithholdingRate = (orderRate: unknown, customerRate: unknown): number => {
  const order = toRate(orderRate);
  if (order !== null) return order > 0 ? order : 0;
  const customer = toRate(customerRate);
  return customer !== null && customer > 0 ? customer : 0;
};

/**
 * Held-back amount in whole cents (half-up) for a base subtotal and rate.
 * Returns 0 for a non-positive base or rate. The result is a magnitude — the
 * caller subtracts it from the balance due. Mirrors
 * withholding.compute_withholding_cents (Math.round is half-up for the positive
 * values involved here, matching Python's ROUND_HALF_UP).
 */
export const computeWithholdingCents = (baseCents: unknown, rate: unknown): number => {
  const r = toRate(rate);
  if (r === null || r <= 0) return 0;
  const base = Math.trunc(Number(baseCents) || 0);
  if (base <= 0) return 0;
  return Math.round(base * r);
};

/**
 * Coerce a manual withholding-amount override (dollars-as-cents already, or
 * null/'' = no override) to whole cents or null. Negative clamps to 0. Mirrors
 * withholding.normalize_amount_cents (the value here is already in cents; the UI
 * converts a dollar entry to cents before calling).
 */
export const normalizeAmountCents = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.round(n));
};

/**
 * Effective held-back amount in whole cents for the order summary. A manual
 * per-order amount override (orders.pr_withholding_amount_cents) wins outright —
 * INCLUDING 0 (zeroes the holdback) — and carries no rate label. Otherwise the
 * rate model: resolve the effective rate and apply it to the pre-tax subtotal.
 * Returns { amountCents, rateLabel } where rateLabel is undefined for a manual
 * override or a zero holdback. Mirrors withholding.resolve_withholding_amount.
 */
export const resolveWithholdingAmount = (
  amountOverride: unknown,
  orderRate: unknown,
  customerRate: unknown,
  subtotalCents: unknown,
): { amountCents: number; rateLabel?: string } => {
  const override = normalizeAmountCents(amountOverride);
  if (override !== null) {
    return { amountCents: override };
  }
  const rate = resolveWithholdingRate(orderRate, customerRate);
  const amountCents = computeWithholdingCents(subtotalCents, rate);
  return { amountCents, rateLabel: amountCents > 0 ? formatRate(rate) : undefined };
};

/**
 * Fraction → percent label, e.g. 0.10 → "10%", 0.065 → "6.5%". Trailing zeros
 * trimmed. Mirrors withholding.format_rate.
 */
export const formatRate = (rate: unknown): string => {
  const r = toRate(rate) ?? 0;
  const pct = Math.round(r * 100 * 100) / 100; // percent, 2dp
  return `${pct}%`;
};
